import { memo, useEffect, useRef, useState } from 'react'
import { Send, Loader2, Sparkles, Trash2, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useChatStore } from '@/stores'
import { useChatStream } from '@/hooks'
import { cn, formatRelativeTime } from '@/utils'
import { Think } from '@ant-design/x'
import XMarkdown, { type ComponentProps } from '@ant-design/x-markdown'
import { toast } from 'sonner'

const markdownContentClassName = cn(
  'max-w-none break-words text-sm leading-6 text-inherit',
  '[&_p]:my-1 [&_p]:whitespace-pre-wrap',
  '[&_h1]:mb-1.5 [&_h1]:mt-2.5 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:leading-6',
  '[&_h2]:mb-1.5 [&_h2]:mt-2.5 [&_h2]:text-[15px] [&_h2]:font-semibold [&_h2]:leading-6',
  '[&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-medium [&_h3]:leading-6',
  '[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-4.5',
  '[&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-4.5',
  '[&_li]:my-0 [&_li>p]:my-0',
  '[&_table]:my-1.5 [&_table]:w-full [&_table]:border-collapse [&_table]:overflow-hidden [&_table]:rounded-md [&_table]:text-left',
  '[&_thead]:bg-slate-200/70 dark:[&_thead]:bg-slate-700/60',
  '[&_th]:border [&_th]:border-slate-300 [&_th]:px-2.5 [&_th]:py-1.5 [&_th]:font-semibold dark:[&_th]:border-slate-600',
  '[&_td]:border [&_td]:border-slate-300 [&_td]:px-2.5 [&_td]:py-1.5 dark:[&_td]:border-slate-600',
  '[&_tr:nth-child(even)]:bg-slate-50/60 dark:[&_tr:nth-child(even)]:bg-slate-800/40',
  '[&_blockquote]:my-1.5 [&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-2.5 [&_blockquote]:text-slate-600 dark:[&_blockquote]:border-slate-600 dark:[&_blockquote]:text-slate-300',
  '[&_pre]:my-1.5 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-slate-900 [&_pre]:px-3 [&_pre]:py-2 [&_pre]:text-slate-100',
  '[&_code]:rounded [&_code]:bg-slate-200 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.82em] dark:[&_code]:bg-slate-700',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_hr]:my-2 [&_hr]:border-slate-200 dark:[&_hr]:border-slate-700',
  '[&_a]:text-indigo-600 [&_a]:underline-offset-2 hover:[&_a]:underline dark:[&_a]:text-indigo-400'
)

const ThinkComponent = memo(({ children, streamStatus }: ComponentProps) => {
  const [expanded, setExpanded] = useState(true)

  useEffect(() => {
    if (streamStatus === 'done') {
      setExpanded(false)
    }
  }, [streamStatus])

  return (
    <Think
      expanded={expanded}
      loading={streamStatus === 'loading'}
      title={streamStatus === 'loading' ? '思考中...' : '思考过程'}
      icon={<Sparkles className="w-4 h-4" />}
      onClick={() => setExpanded((currentExpanded) => !currentExpanded)}
      className="[&_.antd-x-think-content]:!bg-slate-100 dark:[&_.antd-x-think-content]:!bg-slate-800"
    >
      {children}
    </Think>
  )
})

export function ChatPage() {
  const [input, setInput] = useState('')
  const [isHydrated, setIsHydrated] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const { sessions, currentSessionId, addSession } = useChatStore()
  const { sendMessage, isStreaming } = useChatStream()

  const currentSession = sessions.find((s) => s.id === currentSessionId)

  // Handle hydration - only check once on mount
  useEffect(() => {
    const stored = localStorage.getItem('chat_sessions')
    if (stored) {
      setIsHydrated(true)
    } else {
      // No stored data, set hydrated immediately to allow session creation
      setIsHydrated(true)
    }
  }, [])

  // Auto-scroll to bottom
  useEffect(() => {
    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({
        block: 'end',
      })
    }

    scrollToBottom()
    const frameId = requestAnimationFrame(scrollToBottom)

    return () => {
      cancelAnimationFrame(frameId)
    }
  }, [currentSessionId, currentSession?.messages])

  // Create initial session if none exists (only after hydration)
  useEffect(() => {
    if (isHydrated && sessions.length === 0) {
      const newSession = {
        id: Date.now().toString(),
        title: '新对话',
        messages: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      addSession(newSession)
    }
  }, [isHydrated, sessions.length, addSession])

  const handleSend = async () => {
    if (!input.trim() || isStreaming || !currentSessionId) return

    const message = input.trim()
    setInput('')

    // Get history for context
    const history = currentSession?.messages.slice(-10).map((m) => ({
      role: m.role,
      content: m.content,
    })) || []

    await sendMessage(message, history)
  }

  // Quick send - set input and send immediately
  const handleQuickSend = async (prompt: string) => {
    if (isStreaming || !currentSessionId) return

    const history = currentSession?.messages.slice(-10).map((m) => ({
      role: m.role,
      content: m.content,
    })) || []

    await sendMessage(prompt, history)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('已复制到剪贴板')
  }

  const clearCurrentSession = () => {
    if (currentSessionId) {
      const newSession = {
        id: Date.now().toString(),
        title: '新对话',
        messages: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      addSession(newSession)
      toast.success('已清空对话')
    }
  }

  const renderMarkdown = (content: string, loading = false) => (
    <div className={markdownContentClassName}>
      <XMarkdown
        content={content}
        components={{ think: ThinkComponent }}
        paragraphTag="div"
        streaming={{ hasNextChunk: loading }}
        protectCustomTagNewlines
      />
    </div>
  )

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-500" />
            AI 助手
          </h1>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={clearCurrentSession}
          className="gap-2"
        >
          <Trash2 className="w-4 h-4" />
          清空对话
        </Button>
      </div>

      {/* Chat Container */}
      <div className="flex-1 flex flex-col bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
        {/* Messages */}
        <ScrollArea className="flex-1" ref={scrollRef}>
          <div className="p-6 space-y-6">
            {currentSession?.messages.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  开始对话
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mx-auto">
                  我是您的企业助手，可以帮您解答关于用户管理、考勤、绩效、请假和文档管理的问题
                </p>
                <div className="flex flex-wrap justify-center gap-2 mt-6">
                  {[
                    '公司有多少在职员工？',
                    '员工职级分布情况？',
                    '谁有华为的工作经历？',
                    '张三的下属有谁？',
                    '介绍考勤管理制度',
                    '查询下北京今天天气',
                    '介绍下晋升管理办法',
                  ].map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => handleQuickSend(prompt)}
                      className="px-4 py-2 rounded-full bg-slate-100 dark:bg-slate-800 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {currentSession?.messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex gap-4',
                  message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                )}
              >
                {/* Avatar */}
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                    message.role === 'user'
                      ? 'bg-indigo-500 text-white'
                      : 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
                  )}
                >
                  {message.role === 'user' ? (
                    <span className="text-sm font-medium">我</span>
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                </div>

                {/* Message Content */}
                <div
                  className={cn(
                    'group',
                    message.role === 'user'
                      ? 'max-w-[80%] text-right'
                      : 'w-full max-w-3xl text-left'
                  )}
                >
                  <div
                    className={cn(
                      'px-4 py-3 rounded-2xl text-sm',
                      message.role === 'user'
                        ? 'inline-block bg-indigo-500 text-white'
                        : 'block w-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100'
                    )}
                  >
                    {message.role === 'assistant' ? (
                      !message.content && message.isStreaming ? (
                        <div className="flex items-center gap-2 text-slate-400">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>思考中...</span>
                        </div>
                      ) : (
                        <>
                          {renderMarkdown(message.content, Boolean(message.isStreaming))}
                          {message.isStreaming && (
                            <span className="inline-block w-2 h-4 bg-indigo-500 ml-1 animate-pulse" />
                          )}
                        </>
                      )
                    ) : (
                      message.content
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-xs text-slate-400">
                      {formatRelativeTime(message.timestamp)}
                    </span>
                    {message.role === 'assistant' && (
                      <button
                        onClick={() => copyToClipboard(message.content)}
                        className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" />
                        复制
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="shrink-0 p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-3 w-full">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="输入您的问题..."
              disabled={isStreaming}
              className="flex-1 min-h-[44px] max-h-32 resize-none rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2.5 text-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all duration-200 dark:placeholder:text-slate-500"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isStreaming}
              size="icon"
              className="shrink-0 rounded-xl"
            >
              {isStreaming ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2 text-center">
            AI 生成的内容仅供参考，请以实际情况为准
          </p>
        </div>
      </div>
    </div>
  )
}
